import * as React from "react"
import { cva } from "class-variance-authority"
import { cn } from "@/components/lib/utils"
import {
  AlertCircle,
  Terminal,
  CheckCircle,
  AlertTriangle,
  Clock,
  Loader2,
  Info,
} from "lucide-react"

const alertVariants = cva(
  "relative w-full rounded-lg border p-4 pl-11",
  {
    variants: {
      variant: {
        default: "bg-background text-foreground",
        destructive:
          "border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive",
        success:
          "border-green-500/50 bg-green-50 text-green-700 dark:bg-green-900/30 [&>svg]:text-green-600",
        warning:
          "border-yellow-500/50 bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 [&>svg]:text-yellow-600",
        loading:
          "border-primary/50 bg-primary/20 text-primary-700 dark:bg-primary-900/30 [&>svg]:text-primary-600",
        timeout:
          "border-red-500/50 bg-red-50 text-red-700 dark:bg-red-900/30 [&>svg]:text-red-600",
        info:
          "border-blue-500/50 bg-blue-50 text-blue-700 dark:bg-blue-900/30 [&>svg]:text-blue-600",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const variantIcons = {
  default: Terminal,
  destructive: AlertCircle,
  success: CheckCircle,
  warning: AlertTriangle,
  loading: Loader2,
  timeout: Clock,
  info: Info,
}

const Alert = React.forwardRef(({ className, variant = "default", children, ...props }, ref) => {
  const Icon = variantIcons[variant] || Terminal

  return (
    <div
      ref={ref}
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    >
      <Icon className="absolute left-4 top-4 h-5 w-5" />
      {children}
    </div>
  )
})
Alert.displayName = "Alert"

const AlertTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn("mb-1 font-medium leading-none tracking-tight", className)}
    {...props}
  />
))
AlertTitle.displayName = "AlertTitle"

const AlertDescription = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm [&_p]:leading-relaxed", className)}
    {...props}
  />
))
AlertDescription.displayName = "AlertDescription"

export {
  Alert,
  AlertTitle,
  AlertDescription
}
